// Real authentication service using API endpoints
import { buildApiUrl, debugLog } from '../config/environment';

export const login = async (email, password) => {
  debugLog('Login attempt for email:', email);

  try {
    const response = await fetch(buildApiUrl('/login'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Login failed');
    }

    const loginResponse = await response.json();
    debugLog('Login successful:', loginResponse);

    // Return the response directly - AuthContext will handle storage
    return loginResponse;
  } catch (error) {
    debugLog('Login error:', error);
    throw error;
  }
};

/**
 * Finalise a multi-identity login by exchanging the short-lived selection
 * token (returned by /login when requires_context_selection=true) plus a
 * chosen identity for a full LoginResponse with access tokens.
 *
 * @param {Object} params
 * @param {string} params.selectionToken
 * @param {'agent'|'renter'|'owner'} params.contextType
 * @param {number} [params.agentId]
 * @param {number} [params.renterId]
 * @param {number} [params.ownerId]
 * @returns {Promise<Object>} Full LoginResponse (tokens + user)
 */
export const selectLoginContext = async ({ selectionToken, contextType, agentId, renterId, ownerId }) => {
  debugLog('Selecting login context:', contextType);

  const body = {
    selection_token: selectionToken,
    context_type: contextType,
  };
  if (agentId != null) body.agent_id = agentId;
  if (renterId != null) body.renter_id = renterId;
  if (ownerId != null) body.owner_id = ownerId;

  const response = await fetch(buildApiUrl('/login/select-context'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Surface 401 distinctly so the UI can route back to /login with a fresh
    // session-expired message.
    const err = new Error(errorData.message || 'Failed to select login context');
    err.status = response.status;
    throw err;
  }

  return response.json();
};

export const register = async (userData) => {
  debugLog('Register attempt for email:', userData.email);

  try {
    // Map the form fields to the backend's RegisterRequest shape.
    // `user_type` defaults to "owner" for the public self-signup page.
    const body = {
      email: userData.email,
      password: userData.password,
      given: userData.given || userData.first_name,
      surname: userData.surname || userData.last_name,
      user_type: userData.user_type || 'owner',
    };
    if (userData.agency_id) body.agency_id = userData.agency_id;
    if (userData.mobile) body.mobile = userData.mobile;

    const response = await fetch(buildApiUrl('/register'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let message = text;
      try {
        const parsed = JSON.parse(text);
        message = parsed.message || parsed.error || text;
      } catch (_) { /* keep raw text */ }
      throw new Error(message || 'Registration failed');
    }

    // 202 Accepted, body: { message, email, resent, expires_at }
    // No user is created yet — the verification email must be confirmed.
    const registerResponse = await response.json();
    debugLog('Registration accepted — verification email pending:', registerResponse);
    return registerResponse;
  } catch (error) {
    debugLog('Registration error:', error);
    throw error;
  }
};

export const logout = () => {
  debugLog('User logout');
  // Clear session storage including tokens
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('agencyId');
  sessionStorage.removeItem('agencyCurrency');
  sessionStorage.removeItem('currentAgent');
};

export const getCurrentUser = () => {
  try {
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    debugLog('Error parsing user data:', error);
    return null;
  }
};

export const getAuthToken = () => {
  // Prioritize access_token, fallback to token for backward compatibility
  return sessionStorage.getItem('access_token') || sessionStorage.getItem('token');
};

export const isAuthenticated = () => {
  return !!getAuthToken() && !!getCurrentUser();
};

// Helper function to make authenticated API calls
export const makeAuthenticatedRequest = async (url, options = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  headers['Authorization'] = `Bearer ${token}`;

  console.log('[makeAuthenticatedRequest] Making request to:', url);
  console.log('[makeAuthenticatedRequest] With headers:', headers);
  console.log('[makeAuthenticatedRequest] Method:', options.method || 'GET');

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    console.log('[makeAuthenticatedRequest] Response received:', {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    });

    if (!response.ok && response.status === 401) {
      // Token might be expired, could implement refresh logic here
      throw new Error('Authentication failed - token may be expired');
    }

    return response;
  } catch (error) {
    console.error('[makeAuthenticatedRequest] Request failed:', error);
    console.error('[makeAuthenticatedRequest] Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};
